import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

const AuthContext = createContext();

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const IDLE_EVENTS = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const raw = sessionStorage.getItem("user");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      sessionStorage.removeItem("user");
      return null;
    }
  });

  const idleTimer = useRef(null);

  const logout = useCallback(() => {
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    setUser(null);
  }, []);

  // Reset the idle timer on any user activity
  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      logout();
    }, IDLE_TIMEOUT_MS);
  }, [logout]);

  // Start/stop idle tracking based on whether a user is logged in
  useEffect(() => {
    if (!user) {
      clearTimeout(idleTimer.current);
      return;
    }

    resetIdleTimer();
    IDLE_EVENTS.forEach((evt) => window.addEventListener(evt, resetIdleTimer, { passive: true }));

    return () => {
      clearTimeout(idleTimer.current);
      IDLE_EVENTS.forEach((evt) => window.removeEventListener(evt, resetIdleTimer));
    };
  }, [user, resetIdleTimer]);

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
